import {
  getMyTicketsApi,
  getSessionsApi,
  getSessionMessagesApi,
  getTicketStatusApi,
  getTokenUsageApi,
  raiseTicketApi,
  saveMessageApi,
  sendChatMessageApi,
  uploadChatAttachmentApi,
} from "../../api/chatbot";
import { CHAT_ANSWERS, TICKET_KEYWORDS } from "../../components/Chatbot/chatAnswers";
import { API_BASE_URL } from "../../utilities";

// ─── Action Types ─────────────────────────────────────────────────────────────
export const CHAT_PANE_OPEN            = "CHAT_PANE_OPEN";
export const CHAT_PANE_CLOSE           = "CHAT_PANE_CLOSE";
export const HISTORY_VIEW_OPEN         = "HISTORY_VIEW_OPEN";
export const HISTORY_VIEW_CLOSE        = "HISTORY_VIEW_CLOSE";
export const CHAT_NEW                  = "CHAT_NEW";
export const CHAT_ADD_MESSAGE          = "CHAT_ADD_MESSAGE";
export const CHAT_SET_MESSAGES         = "CHAT_SET_MESSAGES";
export const CHAT_SET_LOADING          = "CHAT_SET_LOADING";
export const CHAT_SHOW_QUICK_QUESTIONS = "CHAT_SHOW_QUICK_QUESTIONS";
export const CHAT_HIDE_QUICK_QUESTIONS = "CHAT_HIDE_QUICK_QUESTIONS";
export const QUICK_QUESTIONS_SET       = "QUICK_QUESTIONS_SET";
export const SESSIONS_SET              = "SESSIONS_SET";
export const CHAT_STREAM_START         = "CHAT_STREAM_START";
export const CHAT_STREAM_TOKEN         = "CHAT_STREAM_TOKEN";
export const CHAT_STREAM_DONE          = "CHAT_STREAM_DONE";
export const TICKET_SUBMITTED          = "TICKET_SUBMITTED";
export const TICKETS_PANEL_OPEN        = "TICKETS_PANEL_OPEN";
export const TICKETS_PANEL_CLOSE       = "TICKETS_PANEL_CLOSE";
export const MY_TICKETS_SET            = "MY_TICKETS_SET";
export const MY_TICKETS_LOADING        = "MY_TICKETS_LOADING";
export const TICKET_SELECT             = "TICKET_SELECT";
export const TOKEN_USAGE_SET           = "TOKEN_USAGE_SET";
export const CHATBOT_ACCESS_SET        = "CHATBOT_ACCESS_SET";
export const CHAT_ATTACHMENT_UPLOADING = "CHAT_ATTACHMENT_UPLOADING";

// ─── Chat Pane ────────────────────────────────────────────────────────────────
export const chatPaneOpen  = () => ({ type: CHAT_PANE_OPEN });
export const chatPaneClose = () => ({ type: CHAT_PANE_CLOSE });

// ─── Access (icon visibility + trial/expired gating) ───────────────────────────
export const setChatbotAccess = (access) => ({ type: CHATBOT_ACCESS_SET, payload: access });

export const chatPaneToggle = () => (dispatch, getState) => {
  const { chatPaneOpen: isOpen } = getState().chatbot;
  if (isOpen) {
    dispatch(chatPaneClose());
  } else {
    dispatch(openChatPane());
  }
};

export const openChatPane = () => (dispatch, getState) => {
  dispatch(chatPaneOpen());
  const { currentSessionId } = getState().chatbot;
  if (!currentSessionId) dispatch(startNewChat());
};

export const startNewChat = () => (dispatch) => {
  dispatch({ type: CHAT_NEW, payload: crypto.randomUUID() });
};

export const historyViewOpen = () => (dispatch) => {
  dispatch({ type: HISTORY_VIEW_OPEN });
  dispatch(fetchSessions());
};

export const historyViewClose = () => ({ type: HISTORY_VIEW_CLOSE });
export const showQuickQuestions = () => ({ type: CHAT_SHOW_QUICK_QUESTIONS });

// ─── Pre-defined answer handler (for quick question chips only) ───────────────
export const sendLocalMessage = (text) => async (dispatch, getState) => {
  const trimmed   = text.trim();
  const sessionId = getState().chatbot.currentSessionId;

  dispatch({
    type: CHAT_ADD_MESSAGE,
    payload: { role: "user", content: trimmed, created_on: new Date().toISOString() },
  });
  dispatch({ type: CHAT_HIDE_QUICK_QUESTIONS });

  const predefined = CHAT_ANSWERS[trimmed];
  let botContent   = "";
  let action       = null;
  let isAction     = false;

  if (predefined === "RAISE_TICKET") {
    botContent = "Sure! Fill in the details below to raise a support ticket.";
    action     = "RAISE_TICKET";
    isAction   = true;
  } else if (predefined === "CHECK_TICKET") {
    botContent = "Please enter your ticket ID to check its status.";
    action     = "CHECK_TICKET";
    isAction   = true;
  } else if (predefined) {
    botContent = predefined;
  } else {
    botContent = "Please pick one of the questions above or type your query.";
  }

  dispatch({
    type: CHAT_ADD_MESSAGE,
    payload: {
      role: "assistant", content: botContent,
      action, is_action: isAction,
      created_on: new Date().toISOString(),
    },
  });

  // Persist to backend so session appears in history and ticket raise works
  try {
    await saveMessageApi({
      session_id:   sessionId,
      user_message: trimmed,
      bot_message:  botContent,
      is_action:    isAction,
    });
    dispatch(fetchSessions());
  } catch (_e) { /* silent */ }
};

// ─── LLM streaming handler (for typed input) ─────────────────────────────────
export const sendChatMessage = (text) => async (dispatch, getState) => {
  const trimmed   = text.trim();
  const sessionId = getState().chatbot.currentSessionId;
  const authToken = localStorage.getItem("token");
  const apiBase   = API_BASE_URL;

  dispatch({
    type: CHAT_ADD_MESSAGE,
    payload: { role: "user", content: trimmed, created_on: new Date().toISOString() },
  });
  dispatch({ type: CHAT_HIDE_QUICK_QUESTIONS });
  dispatch({ type: CHAT_STREAM_START });

  // Token queue — fills as SSE chunks arrive, drained one frame at a time
  const tokenQueue  = [];
  let   draining    = false;
  let   streamDone  = false;
  let   donePayload = null;

  // Drain one token per requestAnimationFrame — each frame = one browser paint
  // React 18 does NOT batch across rAF boundaries, so each token renders immediately
  const drainQueue = () => {
    if (draining) return;
    draining = true;

    const step = () => {
      if (tokenQueue.length > 0) {
        dispatch({ type: CHAT_STREAM_TOKEN, payload: tokenQueue.shift() });
        requestAnimationFrame(step); // next token on next paint frame
      } else if (streamDone) {
        // Queue fully drained — now finalise with action/message
        dispatch({ type: CHAT_STREAM_DONE, payload: donePayload });
        dispatch(fetchSessions());
        draining = false;
      } else {
        // Queue empty but stream not done — wait briefly then check again
        draining = false;
      }
    };

    requestAnimationFrame(step);
  };

  try {
    const res = await fetch(`${apiBase}chatbot/stream/`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: authToken },
      body:    JSON.stringify({ session_id: sessionId, message: trimmed }),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const reader  = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer    = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const data = JSON.parse(raw);
          if (data.token) {
            tokenQueue.push(data.token);
            if (!draining) drainQueue();
          }
          if (data.done) {
            streamDone  = true;
            donePayload = {
              action:     data.action     || null,
              ticket_id:  data.ticket_id  || null,
              message:    data.message    || null,
              message_id: data.message_id || null,
            };
            if (data.input_tokens || data.output_tokens) {
              dispatch(fetchTokenUsage());
            }
            if (!draining && tokenQueue.length === 0) {
              dispatch({ type: CHAT_STREAM_DONE, payload: donePayload });
              dispatch(fetchSessions());
            }
          }
          if (data.error) {
            streamDone  = true;
            donePayload = { action: null, ticket_id: null, error: true, message: null };
            if (!draining && tokenQueue.length === 0) {
              dispatch({ type: CHAT_STREAM_DONE, payload: donePayload });
            }
          }
        } catch (_) {}
      }
    }
  } catch (_e) {
    dispatch({ type: CHAT_STREAM_DONE, payload: { action: null, ticket_id: null, error: true } });
  }
};

// ─── Attachments ──────────────────────────────────────────────────────────────
// Uploads a file (image or .xlsx/.xls) attached to the live conversation. The
// backend creates a permanent "attachment" chat message -- pushing the same
// shape into local state here means it renders identically to one loaded from
// session history, and whichever tool call runs next (create_ticket,
// upload_services_excel, upload_clients_excel) picks it up automatically.
export const uploadChatAttachment = (file) => async (dispatch, getState) => {
  const sessionId = getState().chatbot.currentSessionId;
  dispatch({ type: CHAT_ATTACHMENT_UPLOADING, payload: true });
  try {
    const res = await uploadChatAttachmentApi(sessionId, file);
    const { s3_key, url, file_name, content_type, message_id } = res.data;
    dispatch({
      type: CHAT_ADD_MESSAGE,
      payload: {
        id: message_id,
        role: "attachment",
        content: JSON.stringify({ s3_key, url, file_name, content_type }),
        created_on: new Date().toISOString(),
      },
    });
    return res.data;
  } finally {
    dispatch({ type: CHAT_ATTACHMENT_UPLOADING, payload: false });
  }
};

// ─── Token Usage ──────────────────────────────────────────────────────────────
export const fetchTokenUsage = () => async (dispatch) => {
  try {
    const res = await getTokenUsageApi();
    dispatch({ type: TOKEN_USAGE_SET, payload: res.data });
  } catch (_e) { /* silent */ }
};

// ─── Session ──────────────────────────────────────────────────────────────────
export const fetchSessions = () => async (dispatch) => {
  try {
    const res = await getSessionsApi();
    dispatch({ type: SESSIONS_SET, payload: res.data.sessions });
  } catch (_e) { /* silent */ }
};

export const loadSession = (session_id) => async (dispatch) => {
  dispatch({ type: HISTORY_VIEW_CLOSE });
  dispatch({ type: CHAT_NEW, payload: session_id });
  dispatch({ type: CHAT_SET_LOADING, payload: true });
  try {
    const res = await getSessionMessagesApi(session_id);
    dispatch({ type: CHAT_SET_MESSAGES, payload: res.data.messages });
  } catch (_e) { /* silent */ } finally {
    dispatch({ type: CHAT_SET_LOADING, payload: false });
  }
};

// ─── Tickets ──────────────────────────────────────────────────────────────────
export const raiseTicket = (formData) => async (dispatch) => {
  const res = await raiseTicketApi(formData);
  dispatch({ type: TICKET_SUBMITTED, payload: res.data });
  dispatch(fetchMyTickets());
  return res.data;
};

export const fetchTicketStatus = (ticket_id) => async () => {
  const res = await getTicketStatusApi(ticket_id);
  return res.data;
};

export const ticketsPanelOpen  = () => (dispatch) => {
  dispatch({ type: TICKETS_PANEL_OPEN });
  dispatch(fetchMyTickets());
};
export const ticketsPanelClose = () => ({ type: TICKETS_PANEL_CLOSE });
export const ticketSelect      = (ticket_id) => ({ type: TICKET_SELECT, payload: ticket_id });

export const fetchMyTickets = () => async (dispatch) => {
  dispatch({ type: MY_TICKETS_LOADING });
  try {
    const res = await getMyTicketsApi();
    dispatch({ type: MY_TICKETS_SET, payload: res.data.tickets });
  } catch (_e) { /* silent */ }
};
